import React, { useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import { hierarchy, Cluster } from '@visx/hierarchy';
import { LinkHorizontal } from '@visx/shape';
import { Checkbox } from 'js/components/Forms/Checkbox';
import { RootNode, GroupNode, TeamNode } from './Nodes';
import { calculateBreadth } from 'js/utils/cluster';
import {
  childWeight,
  createOrgAssessmentWeightContext,
  rollupOrgChartScore,
} from 'js/utils/org-assessment-rollup';

function teamScore(teamScores, teamId) {
  const scores = teamScores.find((ts) => ts.team_id === teamId)?.scores;
  if (!scores?.length) {
    return undefined;
  }
  return sortBy(scores, (s) => s.date)[scores.length - 1]?.score;
}

function weightForNode(node, type, weightContext, pathToNode) {
  if (!weightContext || pathToNode.length === 0) {
    return null;
  }

  return childWeight(
    weightContext,
    {
      ...node,
      type,
      child_orgs: type === 'org' ? node.child_orgs ?? [] : undefined,
      teams: type === 'org' ? node.teams : undefined,
    },
    pathToNode
  );
}

const scoreNode = (node, teamScores, weightContext, pathToNode = []) => {
  if (!node.child_orgs && !node.teams) {
    return {
      id: node.id,
      name: node.name,
      type: 'team',
      score: teamScore(teamScores, node.id),
      weight: weightForNode(node, 'team', weightContext, pathToNode),
      children: [],
    };
  }

  const orgRow = { ...node, type: 'org' };
  const childPath = [...pathToNode, orgRow];

  const allChildren = [
    ...sortBy(node.child_orgs ?? [], (o) => o.name),
    ...sortBy(node.teams ?? [], (t) => t.name),
  ].map((child) => scoreNode(child, teamScores, weightContext, childPath));

  const score = rollupOrgChartScore(allChildren, weightContext, childPath);

  return {
    id: node.id,
    name: node.name,
    type: 'org',
    score,
    weight: weightForNode(node, 'org', weightContext, pathToNode),
    children: allChildren,
  };
};

const defaultNodeComponents = {
  Root: RootNode,
  Group: GroupNode,
  Team: TeamNode,
};

const OrgTree = ({
  margin = { top: 10, left: 30, bottom: 10, right: 30 },
  width,
  minHeight = 200,
  nodeRadius = 8,
  targetLeafHeight = 25,
  targetClusterMargin = 10,
  teamNameBuffer = 160,
  organization,
  teamScores,
  workspaceId,
  assessmentKey,
  nodeComponents = defaultNodeComponents,
  teamNodeProps = {},
}) => {
  const {
    Root: RootNodeComponent,
    Group: GroupNodeComponent,
    Team: TeamNodeComponent,
  } = { ...defaultNodeComponents, ...nodeComponents };
  const [showWeights, setShowWeights] = useState(false);

  const scoredStructure = useMemo(() => {
    const weightContext = createOrgAssessmentWeightContext(organization);
    return scoreNode(organization, teamScores ?? [], weightContext);
  }, [organization, teamScores]);

  const tree = useMemo(() => hierarchy(scoredStructure), [scoredStructure]);
  const calculatedTargetLeafHeight = useMemo(
    () =>
      typeof targetLeafHeight === 'function'
        ? targetLeafHeight(showWeights)
        : targetLeafHeight,
    [showWeights, targetLeafHeight]
  );

  const height = useMemo(
    () =>
      Math.max(
        minHeight,
        calculateBreadth(tree, calculatedTargetLeafHeight, targetClusterMargin)
      ),
    [tree, calculatedTargetLeafHeight, targetClusterMargin, minHeight]
  );

  const clusterWidth = width - margin.left - margin.right - teamNameBuffer;
  const clusterHeight = height - margin.top - margin.bottom;

  return (
    <div className="org-tree-chart">
      <div className="org-tree-toolbar-wrapper">
        <div className="org-tree-toolbar">
          <Checkbox
            reverse
            checked={showWeights}
            onChange={(e) => setShowWeights(e.target.checked)}
          >
            <span className="text-info">Show weights</span>
          </Checkbox>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" className="org-tree">
        {/* flip height/width for the cluster because we're rendering horizontally instead of vertically */}
        <Cluster size={[clusterHeight, clusterWidth]} root={tree}>
          {(tree) => (
            <g
              className="org-chart"
              transform={`translate(${margin.left},${margin.top})`}
            >
              {tree.links().map((link, i) => (
                <LinkHorizontal
                  key={`link-${i}`}
                  data={link}
                  className="org-tree-link chart-link"
                />
              ))}
              {tree.descendants().map((node, i) => {
                const {
                  depth,
                  data: { type = null },
                } = node;
                const common = {
                  node,
                  radius: nodeRadius,
                  showWeights,
                };

                if (depth === 0) {
                  return <RootNodeComponent key={`node-${i}`} {...common} />;
                }
                if (type === 'org') {
                  return <GroupNodeComponent key={`node-${i}`} {...common} />;
                }
                return (
                  <TeamNodeComponent
                    key={`node-${i}`}
                    {...common}
                    workspaceId={workspaceId}
                    assessmentKey={assessmentKey}
                    {...teamNodeProps}
                  />
                );
              })}
            </g>
          )}
        </Cluster>
      </svg>
    </div>
  );
};

export default OrgTree;
